import React from 'react';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import { useHasActiveTikTokOffer } from '../../hooks/reactQuery/useHasActiveTikTokOffer';
import { useAppSelector } from '../../redux/hooks';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import Button from '../Button/Button';
import { TikTokIcon } from '../Icons';

const BG_COLOR = '#1a1a1a';

/**
 * Card-sized variant of the TikTok promo (no video), sized to match the
 * offer/product cards. The animated glow is clipped inside the card so it
 * stays fully visible even when the page container clips overflow.
 */
export const TikTokOfferCard: React.FC = () => {
  const venue = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online) || false;
  const { hasActiveTikTokOffer } = useHasActiveTikTokOffer();

  const createPath = isOnlineBrand ? '/products/create' : '/offers/create';

  let ctaLabel: string;
  let title: string;
  let description: string;

  if (hasActiveTikTokOffer) {
    if (isOnlineBrand) {
      ctaLabel = 'New TikTok promotion';
      title = 'Create more TikTok promotions';
      description =
        'Keep the momentum and add more to reach new audiences.';
    } else {
      ctaLabel = 'New TikTok offer';
      title = 'Create more TikTok offers';
      description =
        'Keep the momentum and add more to reach new audiences.';
    }
  } else if (isOnlineBrand) {
    ctaLabel = 'Create TikTok offer';
    title = 'Create your first TikTok offer';
    description =
      'Models create TikTok posts about your brand in exchange for your product.';
  } else {
    ctaLabel = 'Create a TikTok offer';
    title = 'Create your first TikTok offer';
    description =
      'Models create TikTok posts about your venue in exchange for your product or service.';
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 345,
        minHeight: '390px',
        maxHeight: '390px',
        height: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: BG_COLOR,
        fontFamily: 'Poppins',
      }}
    >
      {/* Animated TikTok-colored glow, clipped inside the card */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          filter: 'blur(6px)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 700,
            height: 700,
            transform: 'translate(-50%, -50%)',
            background:
              'conic-gradient(from 0deg, transparent 0%, transparent 58%, #00F7EF 72%, #FF004F 86%, transparent 100%)',
            animation: 'tiktokCardBorderSpin 4s linear infinite',
            '@keyframes tiktokCardBorderSpin': {
              to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
            },
          }}
        />
      </Box>

      {/* Dark inner panel, inset to reveal the spinning glow as a ring */}
      <Box
        sx={{
          position: 'absolute',
          inset: '3px',
          zIndex: 1,
          overflow: 'hidden',
          borderRadius: '9px',
          backgroundColor: BG_COLOR,
          // TikTok-style colored glows
          '&::before': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            top: '-35%',
            left: '-30%',
            width: '360px',
            height: '360px',
            background:
              'radial-gradient(circle, rgba(0, 247, 239, 0.45) 0%, rgba(0, 247, 239, 0) 70%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            zIndex: 0,
            bottom: '-35%',
            right: '-30%',
            width: '360px',
            height: '360px',
            background: 'radial-gradient(circle, rgba(255, 0, 79, 0.4) 0%, rgba(255, 0, 79, 0) 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            p: 3,
            pb: '72px',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: 56,
              height: 56,
              borderRadius: '14px',
              backgroundColor: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <TikTokIcon sx={{ fontSize: '30px' }} />
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Typography sx={{ color: '#ffffff', fontSize: '20px', fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              {description}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* CTA aligned with the offer/product "details" buttons (bottom: 13, px: 14) */}
      <Box sx={{ position: 'absolute', zIndex: 2, left: 14, right: 14, bottom: 13 }}>
        <Button
          component={Link}
          to={createPath}
          variant='contained'
          size='large'
          fullWidth
          sx={{
            fontWeight: 500,
            backgroundColor: '#ffffff',
            color: '#000000',
            '&:hover': { backgroundColor: '#f0f0f0' },
          }}
          startIcon={<TikTokIcon sx={{ fontSize: '18px' }} />}
        >
          {ctaLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default TikTokOfferCard;
